// Revenue splitting — per-gallery allocations, set person by person (owner decision 22 Oct 2026) + payouts.
// (photohawk-serverless docs/revenue-splitting-design.md §0000; lib revenueSplitConfig / revenueSplitPayouts)
//
//   PUT  /admin/engines { guid, setRevenueSplit: true, revenueSplit }       admin.api   — save a gallery's split
//   PUT  /admin/account { setRevenueSplitDefault: true, revenueSplitDefault } admin.api — the workspace default list
//   POST /admin/engines/{guid}/revenue-split/preview                         billing.api — an example order's split,
//                                                                            computed by the server's real allocation
//   GET  /admin/payouts/photographers                                         billing.api — payees' payout status
//   POST /admin/payees/{guid}/invite                                          billing.api — resend an external invite
//   GET  /admin/me/earnings · /admin/me/payouts/link                          billing.api — the signed-in user
//   GET  /payees/earnings?token= · /payees/payouts/link?token=                billing.api — an external payee (magic link)
//
// Payout status: the three GETs above take `refresh=1` to re-check it live with Trolley (the server throttles
// it per person). There's no Trolley webhook; the pages ask when they need to (usePayoutStatusPolling).
//
// A gallery splits nothing unless `enabled`. Each allocation names who gets paid (a team member, or someone
// outside invited by email), a % of the amount after Photohawk's commission and the card fee, and which
// sales it covers: all sales, the photos the member took (credited to them — see lib/api/photoCredits:
// their collection, their upload, a folder or a camera; API scope "uploaded"), or sales from chosen collections.
// There is no built-in "photographer of the photo" row. Allocations add up PER PHOTO: for any one photo the
// matching rows come to at most 100 % (the server checks every case too); the workspace keeps the rest and
// every rounding remainder. Phase 2 (not built): fixed amounts and time windows — same row shape.
//
// Groups (22 Oct 2026): a row can pay a group of 2–20 team members — "Equally" (every member, evenly) or "By
// contribution" (the member each sold photo is credited to; when that person isn't in the group, or nobody is
// credited, every member evenly). Decided per sale and snapshotted on it. A group row counts once per photo
// in the ≤ 100 % check; each member is paid as their own payee (own payout status). No "Photos they took"
// scope for a group — "By contribution" is that.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { photohawkApi } from "@/lib/photohawkApi";
import { tenantHostname } from "@/lib/apiDomain";
import type { TenantScope } from "@/lib/api/galleries";

export type AllocationRole = "partner" | "sponsor" | "club" | "charity" | "venue" | "photographer" | "other";
/** "Kind" of someone outside the workspace (the sheet's picker). */
export const ALLOCATION_ROLES: { value: AllocationRole; label: string }[] = [
  { value: "partner", label: "Partner" },
  { value: "sponsor", label: "Sponsor" },
  { value: "club", label: "Club" },
  { value: "charity", label: "Charity" },
  { value: "venue", label: "Venue" },
  { value: "photographer", label: "Photographer" },
  { value: "other", label: "Other" },
];
export const MAX_ALLOCATIONS = 30;

export type GroupDistribution = "equal" | "contribution";
export const MIN_GROUP_MEMBERS = 2;
export const MAX_GROUP_MEMBERS = 20;
export const DISTRIBUTION_LABEL: Record<GroupDistribution, string> = { equal: "Equally", contribution: "By contribution" };
/** How a group shares its row, in a sentence. */
export const DISTRIBUTION_HINT: Record<GroupDistribution, string> = {
  equal: "Every member gets the same part of each sale.",
  contribution: "Each sale goes to the member the photo is credited to. If they're not in the group, or nobody is credited, the members share it equally.",
};

export type AllocationPayee =
  | { kind: "user"; userGuid: string }
  | { kind: "external"; email: string; name?: string; payeeGuid?: string }
  | { kind: "group"; members: string[]; distribution: GroupDistribution };

/** Every payee key a row pays ('u:<userGuid>' / 'p:<payeeGuid>'): one, or each member of a group. */
export const recipientsOf = (a: Allocation): string[] =>
  a.payee.kind === "user" ? [`u:${a.payee.userGuid}`]
    : a.payee.kind === "group" ? a.payee.members.map((u) => `u:${u}`)
    : a.payee.payeeGuid ? [`p:${a.payee.payeeGuid}`] : [];

export type AllocationScope = { type: "all" } | { type: "uploaded" } | { type: "collections"; collectionGuids: string[] };
export type ScopeType = AllocationScope["type"];

export interface Allocation {
  id?: string;
  /** The name on statements. */
  name: string;
  role?: AllocationRole;
  payee: AllocationPayee;
  /** Only "percent" for now (fixed per photo / per order are Phase 2). */
  share: { type: "percent"; percent: number };
  scope: AllocationScope;
}

export interface GallerySplit {
  enabled: boolean;
  allocations: Allocation[];
  /** Set by the move to allocations: the owner must look at the list and save (splitting is paused until then). */
  needsReview?: boolean;
  /** What the migration converted (wasOn: the gallery split before it was paused for review). */
  migratedFrom?: string | { kind?: string; percent?: number; wasOn?: boolean };
  setAt?: number;
  setBy?: string | null;
}

/** TENANT.revenueSplitDefault — a pre-fill list for galleries, never a rule (no collections). */
export interface WorkspaceSplitDefault {
  allocations: Allocation[];
  setAt?: number;
  setBy?: string | null;
}

/** Older shapes, read only (the server converts them the same way). */
interface LegacyParty {
  id?: string;
  type?: "photographer" | "party";
  name?: string;
  role?: string;
  percent?: number;
  payee?: AllocationPayee | null;
}

const isRole = (r: unknown): r is AllocationRole => ALLOCATION_ROLES.some((x) => x.value === r);

/**
 * The gallery's split in today's shape (mirrors the server's effectiveSplit): named legacy parties become
 * "all sales" rows; a "photographer of the photo" party or a legacy gallery % pauses the split for review.
 */
export const effectiveSplit = (
  engine: { revenueSplit?: (Partial<GallerySplit> & { parties?: unknown[] }) | null; photographerSharePercent?: number | null } | null | undefined,
): GallerySplit | null => {
  if (!engine) return null;
  const rs = engine.revenueSplit;
  if (rs && typeof rs === "object") {
    if (Array.isArray(rs.allocations)) return { enabled: !!rs.enabled, allocations: rs.allocations, ...(rs.needsReview ? { needsReview: true } : {}) };
    const parties = (Array.isArray(rs.parties) ? rs.parties : []) as LegacyParty[];
    const allocations: Allocation[] = parties
      .filter((p) => p && p.type === "party" && p.payee)
      .map((p) => ({ id: p.id, name: p.name || "Party", ...(isRole(p.role) ? { role: p.role } : {}), payee: p.payee as AllocationPayee,
        share: { type: "percent", percent: Number(p.percent) || 0 }, scope: { type: "all" } }));
    const photographer = parties.some((p) => p && p.type === "photographer" && Number(p.percent) > 0);
    return photographer ? { enabled: false, allocations, needsReview: true } : { enabled: !!rs.enabled, allocations };
  }
  const legacy = Number(engine.photographerSharePercent);
  if (engine.photographerSharePercent !== null && engine.photographerSharePercent !== undefined && legacy > 0) return { enabled: false, allocations: [], needsReview: true };
  return null;
};

export const percentOf = (a: Allocation) => Number(a.share?.percent) || 0;

/**
 * An allocation's share as typed (mirrors the server's allocationPercent): a number more than 0 and at most
 * 100, with at most 2 decimals.
 */
export const parseAllocationPercent = (input: string | number | null | undefined): { ok: true; value: number } | { ok: false; error: string } => {
  const s = input === null || input === undefined ? "" : String(input).trim();
  if (!s) return { ok: false, error: "Enter a share" };
  if (!/^\d+(\.\d+)?$/.test(s)) return { ok: false, error: "The share must be a number, like 12.5" };
  const n = Number(s);
  if (!(n > 0)) return { ok: false, error: "The share must be more than 0%" };
  if (n > 100) return { ok: false, error: "The share can be at most 100%" };
  if (!/^\d+(\.\d{1,2})?$/.test(s)) return { ok: false, error: "The share can have at most 2 decimals" };
  return { ok: true, value: Math.round(n * 100) / 100 };
};
const bp = (n: number) => Math.round(n * 100);
const pct = (b: number) => Number((b / 100).toFixed(2));

/** Does `a` apply to a photo { creditedUserGuid, collectionGuid }? (mirrors the server's matches) */
export const allocationMatches = (a: Allocation, photo: { creditedUserGuid?: string | null; collectionGuid?: string | null }) => {
  if (a.scope.type === "all") return true;
  if (a.scope.type === "uploaded") return a.payee.kind === "user" && !!photo.creditedUserGuid && a.payee.userGuid === photo.creditedUserGuid;
  return !!photo.collectionGuid && a.scope.collectionGuids.includes(photo.collectionGuid);
};

export interface PhotoCase {
  /** The member the photo is credited to; null = nobody, or a member without a "Photos they took" row. */
  creditedUserGuid: string | null;
  /** null = any collection no row names. */
  collectionGuid: string | null;
  percent: number;
  /** The rows that apply, in list order. */
  rows: { index: number; percent: number }[];
}

/**
 * Every distinct photo case a list can meet (mirrors the server's photoCases): (each member with a
 * "Photos they took" row, or nobody's) × (each listed collection, or any other). A photo is credited to one
 * member at most — through its collection, its upload, a folder or a camera — so this covers every photo.
 */
export const photoCases = (allocations: Allocation[]): PhotoCase[] => {
  const uploaders: (string | null)[] = [null];
  const collections: (string | null)[] = [null];
  for (const a of allocations) {
    if (a.scope.type === "uploaded" && a.payee.kind === "user" && !uploaders.includes(a.payee.userGuid)) uploaders.push(a.payee.userGuid);
    if (a.scope.type === "collections") for (const c of a.scope.collectionGuids) if (!collections.includes(c)) collections.push(c);
  }
  const out: PhotoCase[] = [];
  for (const u of uploaders) for (const c of collections) {
    const rows = allocations.map((a, index) => ({ a, index })).filter(({ a }) => allocationMatches(a, { creditedUserGuid: u, collectionGuid: c }))
      .map(({ a, index }) => ({ index, percent: percentOf(a) }));
    out.push({ creditedUserGuid: u, collectionGuid: c, percent: pct(rows.reduce((t, r) => t + bp(r.percent), 0)), rows });
  }
  return out;
};

/** The most any one photo pays out, in %. */
export const maxPercent = (allocations: Allocation[]) => Math.max(0, ...photoCases(allocations).map((c) => c.percent));

/** The "uploaded" scope's name in the UI (the API value stays "uploaded"). */
export const TOOK_LABEL = "Photos they took (uploaded or credited)";

/** "All sales in this gallery" · "Photos they took (uploaded or credited)" · "Sales from: Finish line, Podium". */
export const scopeLabel = (scope: AllocationScope, collectionName: (guid: string) => string | undefined = () => undefined) => {
  if (scope.type === "all") return "All sales in this gallery";
  if (scope.type === "uploaded") return TOOK_LABEL;
  const names = scope.collectionGuids.map((g) => collectionName(g) ?? "a removed collection");
  return names.length ? `Sales from: ${names.join(", ")}` : "Sales from: pick collections";
};

export interface AllocationForm {
  who: "member" | "group" | "outside";
  userGuid: string;
  /** A group's members (who: "group"). */
  members: string[];
  distribution: GroupDistribution;
  name: string;
  email: string;
  role: AllocationRole;
  percent: string;
  scope: ScopeType;
  collectionGuids: string[];
  payeeGuid?: string;
  id?: string;
}

export const emptyForm = (): AllocationForm => ({ who: "member", userGuid: "", members: [], distribution: "contribution", name: "", email: "", role: "partner", percent: "", scope: "uploaded", collectionGuids: [] });

export const toForm = (a: Allocation): AllocationForm => ({
  who: a.payee.kind === "user" ? "member" : a.payee.kind === "group" ? "group" : "outside",
  userGuid: a.payee.kind === "user" ? a.payee.userGuid : "",
  members: a.payee.kind === "group" ? [...a.payee.members] : [],
  distribution: a.payee.kind === "group" ? a.payee.distribution : "contribution",
  name: a.name,
  email: a.payee.kind === "external" ? a.payee.email : "",
  role: a.role ?? "other",
  percent: String(percentOf(a)),
  scope: a.scope.type,
  collectionGuids: a.scope.type === "collections" ? a.scope.collectionGuids : [],
  ...(a.payee.kind === "external" && a.payee.payeeGuid ? { payeeGuid: a.payee.payeeGuid } : {}),
  ...(a.id ? { id: a.id } : {}),
});

/** Validate the add / edit sheet (mirrors the server's per-row rules). */
export const fromForm = (f: AllocationForm, { allowCollections = true } = {}): { ok: true; value: Allocation } | { ok: false; errors: string[] } => {
  const errors: string[] = [];
  const name = f.name.trim();
  if (f.who === "member" && !f.userGuid) errors.push("Choose the team member to pay");
  const members = [...new Set(f.members)];
  if (f.who === "group" && members.length < MIN_GROUP_MEMBERS) errors.push(`Pick at least ${MIN_GROUP_MEMBERS} team members for the group`);
  if (f.who === "group" && members.length > MAX_GROUP_MEMBERS) errors.push(`At most ${MAX_GROUP_MEMBERS} people in a group`);
  if (!name) errors.push(f.who === "group" ? "Give the group a name" : "A name is required");
  const email = f.email.trim().toLowerCase();
  if (f.who === "outside" && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errors.push("Enter a valid email so we can invite them to set up payouts");
  const p = parseAllocationPercent(f.percent);
  if ("error" in p) errors.push(p.error);
  if (f.scope === "uploaded" && f.who === "group") errors.push('A group shares "By contribution" instead of "Photos they took"');
  else if (f.scope === "uploaded" && f.who !== "member") errors.push('"Photos they took" is for team members only');
  if (f.scope === "collections" && !allowCollections) errors.push("Collections can only be picked on a gallery");
  else if (f.scope === "collections" && !f.collectionGuids.length) errors.push("Pick at least one collection");
  if (errors.length || !p.ok) return { ok: false, errors };
  const payee: AllocationPayee = f.who === "member" ? { kind: "user", userGuid: f.userGuid }
    : f.who === "group" ? { kind: "group", members, distribution: f.distribution }
    : { kind: "external", email, name, ...(f.payeeGuid ? { payeeGuid: f.payeeGuid } : {}) };
  const scope: AllocationScope = f.scope === "collections" ? { type: "collections", collectionGuids: f.collectionGuids } : { type: f.scope };
  return { ok: true, value: { ...(f.id ? { id: f.id } : {}), name: name.slice(0, 80), ...(f.who === "outside" ? { role: f.role } : {}), payee, share: { type: "percent", percent: p.value }, scope } };
};

export interface OverCase extends PhotoCase {
  /** "Photos credited to Alex in Finish line would pay out 105%." */
  message: string;
  /** "Lower Alex's share to 85% or less, or remove the sponsor from Finish line." */
  fix: string;
}

/**
 * The whole list: the per-photo ≤ 100 % check (mirrors the server, SPLIT_OVER_100) and the rows that still
 * need a pick (a copied collection scope with no match). `names` label members and collections.
 */
export const checkSplit = (
  enabled: boolean,
  allocations: Allocation[],
  names: { member?: (userGuid: string) => string | undefined; collection?: (guid: string) => string | undefined } = {},
): { ok: boolean; errors: string[]; over: OverCase[]; cases: PhotoCase[]; rowErrors: Record<number, string> } => {
  const errors: string[] = [];
  // Each row's share on its own (e.g. a migrated 0 % row): shown inline on the row, and Save stays off.
  const rowErrors: Record<number, string> = {};
  allocations.forEach((a, i) => {
    const p = parseAllocationPercent(a.share?.percent);
    if ("error" in p) { rowErrors[i] = p.error; errors.push(`${a.name}: ${p.error.charAt(0).toLowerCase()}${p.error.slice(1)}`); }
  });
  if (allocations.length > MAX_ALLOCATIONS) errors.push(`At most ${MAX_ALLOCATIONS} allocations`);
  allocations.forEach((a) => { if (a.scope.type === "collections" && !a.scope.collectionGuids.length) errors.push(`${a.name}: pick the collections again`); });
  if (enabled && !allocations.length) errors.push("Add at least one allocation before turning the split on");
  const cases = photoCases(allocations);
  const who = (u: string | null) => {
    if (!u) return null;
    const row = allocations.find((a) => a.scope.type === "uploaded" && a.payee.kind === "user" && a.payee.userGuid === u);
    return row?.name || names.member?.(u) || "a team member";
  };
  const where = (c: string | null) => (c ? names.collection?.(c) ?? "a collection" : null);
  const over: OverCase[] = cases.filter((c) => c.percent > 100).map((c) => {
    const w = who(c.creditedUserGuid);
    const coll = where(c.collectionGuid);
    const message = `${w ? `Photos credited to ${w}` : "Photos credited to no team member"}${coll ? ` in ${coll}` : ""} would pay out ${c.percent}%.`;
    const extra = c.percent - 100;
    const biggest = [...c.rows].sort((x, y) => y.percent - x.percent)[0];
    const big = allocations[biggest.index];
    const target = Math.max(0, pct(bp(biggest.percent) - bp(extra)));
    const collRow = c.rows.map((r) => allocations[r.index]).find((a) => a.scope.type === "collections" && a !== big);
    const fix = `Lower ${big.name}'s share to ${target}% or less${collRow && coll ? `, or remove ${collRow.name} from ${coll}` : ""}.`;
    return { ...c, message, fix };
  });
  for (const o of over) errors.push(o.message.replace(/\.$/, "") + " — at most 100%");
  return { ok: !errors.length, errors, over, cases, rowErrors };
};

export interface SplitReduction {
  label: string;
  from: number;
  /** 0 when the row is removed or splitting is turned off. */
  to: number;
}

/**
 * Who loses share for future sales when `next` replaces the saved split: a saved row lowered or removed, or
 * every row when splitting is turned off. Empty when the saved split isn't on (nobody has earned from it) or
 * nothing goes down. Past sales keep the split they were sold with (the server snapshots it per photo).
 */
export const splitReductions = (saved: GallerySplit | null, next: GallerySplit): SplitReduction[] => {
  if (!saved?.enabled) return [];
  const out: SplitReduction[] = [];
  for (const a of saved.allocations) {
    const from = percentOf(a);
    if (!(from > 0)) continue;
    const now = next.enabled && a.id ? next.allocations.find((b) => b.id === a.id) : undefined;
    const to = now ? percentOf(now) : 0;
    if (to < from) out.push({ label: a.name, from, to });
  }
  return out;
};

let seq = 0;
const freshId = () => `a${Date.now().toString(36)}${(seq++).toString(36)}`;

/**
 * Copy another gallery's (or the workspace default's) rows into this gallery. Copying never links galleries:
 * new ids, payees kept. Collection scopes are matched by name; a scope with no match keeps no collections, so
 * the row is flagged for the owner to pick again. Returns the rows + how many need a pick.
 */
export const copyAllocations = (
  source: Allocation[],
  sourceCollectionName: (guid: string) => string | undefined,
  targetCollections: { guid: string; name: string }[],
): { allocations: Allocation[]; unmatched: number } => {
  const byName = new Map(targetCollections.map((c) => [c.name.trim().toLowerCase(), c.guid]));
  let unmatched = 0;
  const allocations = source.map((a) => {
    const copy: Allocation = { ...a, id: freshId(), payee: a.payee.kind === "group" ? { ...a.payee, members: [...a.payee.members] } : { ...a.payee }, share: { ...a.share } };
    if (a.scope.type === "collections") {
      const guids = a.scope.collectionGuids.map((g) => byName.get((sourceCollectionName(g) ?? "").trim().toLowerCase())).filter((g): g is string => !!g);
      if (!guids.length) unmatched += 1;
      copy.scope = { type: "collections", collectionGuids: [...new Set(guids)] };
    } else copy.scope = { ...a.scope };
    return copy;
  });
  return { allocations, unmatched };
};

export { freshId as newAllocationId };

const keyOf = (scope: TenantScope) => scope.alias ?? scope.customDomain ?? "current";

export interface SaveSplitResult {
  guid: string;
  revenueSplit: GallerySplit;
  invited: string[];
}

/** The server's refusal of a split: per-photo cases over 100 % (SPLIT_OVER_100) or other rule breaks (SPLIT_INVALID). */
export interface SplitRefusal {
  code?: "SPLIT_OVER_100" | "SPLIT_INVALID" | "SPLIT_UNAVAILABLE_IN_COUNTRY" | string;
  message?: string;
  errors?: string[];
  cases?: { creditedUserGuid?: string | null; uploaderUserGuid?: string | null; collectionGuid: string | null; percent: number; allocationIds: string[] }[];
  /** Per row: { index, id, field: "name" | "payee" | "percent" | "scope", message }. */
  fieldErrors?: { index: number; id: string | null; field: "name" | "payee" | "percent" | "scope"; message: string }[];
}

export const saveGallerySplit = (scope: TenantScope, engineGuid: string, revenueSplit: GallerySplit) =>
  photohawkApi.put<SaveSplitResult>("/admin/engines", {
    service: "admin",
    hostname: tenantHostname(scope),
    body: { guid: engineGuid, setRevenueSplit: true, revenueSplit: { enabled: revenueSplit.enabled, allocations: revenueSplit.allocations } },
  });

export const useSaveGallerySplit = (scope: TenantScope) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ engineGuid, split }: { engineGuid: string; split: GallerySplit }) => saveGallerySplit(scope, engineGuid, split),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["engines", keyOf(scope)] });
      qc.invalidateQueries({ queryKey: ["engine", keyOf(scope)] });
      qc.invalidateQueries({ queryKey: ["photographerSales", keyOf(scope)] });
      qc.invalidateQueries({ queryKey: ["photographerPayouts", keyOf(scope)] });
    },
  });
};

export const saveWorkspaceSplitDefault = (scope: TenantScope, allocations: Allocation[]) =>
  photohawkApi.put<{ revenueSplitDefault: WorkspaceSplitDefault }>("/admin/account", {
    service: "admin",
    hostname: tenantHostname(scope),
    body: { setRevenueSplitDefault: true, revenueSplitDefault: { allocations } },
  });

export const useSaveWorkspaceSplitDefault = (scope: TenantScope) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (allocations: Allocation[]) => saveWorkspaceSplitDefault(scope, allocations),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["account", keyOf(scope)] });
      qc.invalidateQueries({ queryKey: ["photographerPayouts", keyOf(scope)] });
    },
  });
};

// --- preview -------------------------------------------------------------------------------------

export interface ExampleLine {
  type: "Download" | "Print";
  /** Line price in the gallery's currency (major units). */
  price: number;
  photos: number;
  /** Print lab cost (major units). */
  labCost?: number;
}

export interface ExampleOrder {
  lines?: ExampleLine[];
  total?: number;
  photos?: number;
  /** The member the example photos are credited to; null = nobody. */
  creditedUserGuid?: string | null;
  /**
   * Who uploaded the example photos (null = nobody: FTP, imports). Sent instead of creditedUserGuid: the server
   * credits them like a real sale — the collection's assigned photographer, else the uploader.
   */
  uploadedByUserGuid?: string | null;
  /** The example photos' collection; null = none of the listed ones. */
  collectionGuid?: string | null;
}

export interface PreviewParty {
  id: string;
  name: string;
  role?: AllocationRole | null;
  percent: number;
  scope: ScopeType;
  payeeKind?: "user" | "external" | "group" | null;
  amount: number;
  /** A group row's member line: the row's id, the member and how the group shares. */
  groupId?: string;
  userGuid?: string;
  distribution?: GroupDistribution;
}

export interface PreviewScenario {
  collection: "platform" | "application_fee";
  label: string;
  buyerPays: number;
  processingFee: number;
  processingFeeEstimated: boolean;
  /** The scheduled processing fee comes off before the split (fixed at sale; lib/transactionFeeSchedule). */
  processingFeeInBasis: boolean;
  commission: number;
  labCost: number;
  /** The amount the allocations' % apply to: after commission, lab and the card fee. */
  basis?: number;
  applicationFee?: number;
  parties: PreviewParty[];
  workspaceKeeps: number;
  capped?: boolean;
}

export interface SplitPreview {
  currency: string;
  commissionPercent: number;
  tier: "A" | "B" | "C";
  available: boolean;
  scenarios: PreviewScenario[];
  /** Who the example photos were credited to (source: collection | uploader), or null. */
  credited?: { userGuid: string; source: "collection" | "uploader" | "folder" | "camera" } | null;
}

export type PreviewInput = { revenueSplit: { enabled: boolean; allocations: Allocation[] }; example: ExampleOrder; currency?: string };

export const previewGallerySplit = (scope: TenantScope, engineGuid: string, input: PreviewInput, signal?: AbortSignal) =>
  photohawkApi.post<SplitPreview>(`/admin/engines/${encodeURIComponent(engineGuid)}/revenue-split/preview`, {
    service: "billing",
    hostname: tenantHostname(scope),
    body: input,
    signal,
  });

export const useSplitPreview = (scope: TenantScope, engineGuid: string | undefined, input: PreviewInput | null) =>
  useQuery({
    queryKey: ["revenueSplitPreview", keyOf(scope), engineGuid ?? null, JSON.stringify(input)],
    queryFn: ({ signal }) => previewGallerySplit(scope, engineGuid as string, input as PreviewInput, signal),
    enabled: !!engineGuid && !!input && (!!scope.alias || !!scope.customDomain),
    staleTime: 60_000,
    retry: false,
  });

// --- payouts --------------------------------------------------------------------------------------

export type PayoutStatus = "not_onboarded" | "pending" | "payable" | "blocked";

export const PAYOUT_STATUS_LABEL: Record<PayoutStatus, string> = {
  not_onboarded: "Payouts not set up",
  pending: "Setting up",
  payable: "Payout account ready",
  blocked: "Blocked",
};

export interface RecipientCurrency {
  currency: string;
  paid: number;
  pending: number;
  settling?: number;
  held: number;
}

export interface WorkspaceRecipient {
  recipient: string;
  kind: "user" | "external";
  userGuid: string | null;
  name: string | null;
  member: boolean;
  payoutStatus: PayoutStatus;
  currencies: RecipientCurrency[];
}

export interface WorkspacePayoutsView {
  enabled: boolean;
  since: number | null;
  collection: "platform" | "application_fee" | null;
  minimumPayoutUsd: number;
  recipients: WorkspaceRecipient[];
}

const refreshParam = (refresh?: boolean) => (refresh ? { refresh: 1 } : undefined);

export const fetchPhotographerPayouts = (scope: TenantScope, refresh?: boolean, signal?: AbortSignal) =>
  photohawkApi.get<WorkspacePayoutsView>("/admin/payouts/photographers", { service: "billing", hostname: tenantHostname(scope), params: refreshParam(refresh), signal });

export const usePhotographerPayouts = (scope: TenantScope, enabled = true) =>
  useQuery({
    queryKey: ["photographerPayouts", keyOf(scope)],
    queryFn: ({ signal }) => fetchPhotographerPayouts(scope, false, signal),
    enabled: enabled && (!!scope.alias || !!scope.customDomain),
    staleTime: 60_000,
    retry: false,
  });

export const resendPayeeInvite = (scope: TenantScope, payeeGuid: string) =>
  photohawkApi.post<{ invited: boolean }>(`/admin/payees/${encodeURIComponent(payeeGuid)}/invite`, {
    service: "billing",
    hostname: tenantHostname(scope),
  });

export interface EarningsWorkspace {
  recipient: string;
  tenant: string;
  workspaceName: string | null;
  currency: string;
  balance: number;
  pending: number;
  held: number;
  paid: number;
  orders: number;
}

export interface EarningsStatement {
  guid: string;
  runDate: string;
  currency: string;
  amount: number;
  status: "batched" | "paid" | "failed";
  paidAt: number | null;
  workspaces: { workspaceName: string | null; amount: number }[];
}

export interface Earnings {
  recipients: { recipient: string; kind: "user" | "external"; name: string | null; payoutStatus: PayoutStatus }[];
  payoutStatus: PayoutStatus;
  hasEarnings: boolean;
  /** Money held only until payouts are set up, per currency, largest first (empty once set up). */
  waitingForSetup?: { currency: string; amount: number }[];
  /** Named in a split that's on in one of their workspaces (GET /admin/me/earnings only). */
  inSplit?: boolean;
  minimumPayoutUsd: number;
  workspaces: EarningsWorkspace[];
  statements: EarningsStatement[];
  payee?: { name: string; emailAddress: string };
}

export const fetchMyEarnings = (refresh?: boolean, signal?: AbortSignal) =>
  photohawkApi.get<Earnings>("/admin/me/earnings", { service: "billing", params: refreshParam(refresh), signal });

/** The signed-in user's earnings. One cache entry for the whole app: the Earnings tab, the account menu and the
 *  "money waiting" banner share it (they pass a longer `staleTime`, so moving between pages doesn't refetch). */
export const useMyEarnings = (enabled = true, staleTime = 60_000) =>
  useQuery({
    queryKey: ["myEarnings"],
    queryFn: ({ signal }) => fetchMyEarnings(false, signal),
    enabled,
    staleTime,
    retry: false,
  });

export const getMyPayoutLink = async () =>
  (await photohawkApi.get<{ link?: string }>("/admin/me/payouts/link", { service: "billing" }))?.link ?? null;

export const fetchPayeeEarnings = (token: string, refresh?: boolean, signal?: AbortSignal) =>
  photohawkApi.get<Earnings>("/payees/earnings", { service: "billing", anonymous: true, params: { token, ...refreshParam(refresh) }, signal });

export const usePayeeEarnings = (token: string | null) =>
  useQuery({
    queryKey: ["payeeEarnings", token],
    queryFn: ({ signal }) => fetchPayeeEarnings(token as string, false, signal),
    enabled: !!token,
    retry: false,
  });

export const getPayeePayoutLink = async (token: string) =>
  (await photohawkApi.get<{ link?: string }>("/payees/payouts/link", { service: "billing", anonymous: true, params: { token } }))?.link ?? null;

/** Re-reads a payouts query with `refresh=1` (live payout status) into the same cache entry. */
export const useRefreshPhotographerPayouts = (scope: TenantScope) => {
  const qc = useQueryClient();
  return () => qc.fetchQuery({ queryKey: ["photographerPayouts", keyOf(scope)], queryFn: () => fetchPhotographerPayouts(scope, true), staleTime: 0 });
};

export const useRefreshMyEarnings = () => {
  const qc = useQueryClient();
  return () => qc.fetchQuery({ queryKey: ["myEarnings"], queryFn: () => fetchMyEarnings(true), staleTime: 0 });
};

export const useRefreshPayeeEarnings = (token: string | null) => {
  const qc = useQueryClient();
  return () => (token ? qc.fetchQuery({ queryKey: ["payeeEarnings", token], queryFn: () => fetchPayeeEarnings(token, true), staleTime: 0 }) : Promise.resolve(undefined));
};
